import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import OwnInnIcon from '@/components/icons/OwnInnIcon';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import ELoadingButton from '@/components/everridge/ELoadingButton';
import ETextInput from '@/components/everridge/ETextInput';
import { useSignUpApi } from '@/features/identity/hooks/useSignUpApi';
import EPasswordInput from '@/components/everridge/EPasswordInput';

const errorKeys = {
    passwordValidation: 'Dto.Password',
    alreadyRegisteredEmail: 'Identity.Register.OwnerAlreadyRegistered',
    notFoundEmail: 'Identity.Register.OwnerNotFound',
};

type SignUpDto = {
    email: string;
    password: string;
};

const SignUpFormSchema = z
    .object({
        email: z
            .string({
                message: 'elements.form.email.error',
            })
            .email(),
        password: z.string({
            message: 'elements.form.password.error',
        }),
        confirmPassword: z.string({
            message: 'elements.form.confirmPassword.error',
        }),
    })
    .refine((data) => data.password === data.confirmPassword, {
        path: ['confirmPassword'],
        message: 'elements.form.passwordsDifference.error',
    });

type SignUpFormData = z.infer<typeof SignUpFormSchema>;

const SignUpForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const [searchParams] = useSearchParams();
    const email = searchParams.get('email');
    const tenant = searchParams.get('tenant') ?? '';

    const { signUp } = useSignUpApi({ tenant });

    const { control, handleSubmit, setError } = useForm<SignUpFormData>({
        resolver: zodResolver(SignUpFormSchema),
        defaultValues: {
            email: email ?? '',
        },
    });

    const handleFormSubmit = async (form: SignUpFormData) => {
        const dto: SignUpDto = {
            email: form.email,
            password: form.password,
        };
        await signUp(dto)
            .then(() => {
                navigate('/auth/sign-up-confirm');
            })
            .catch((error) => {
                const errorData = error.response.data;
                const errorKey = Object.keys(errorData.errors)[0];
                const errorMessage = errorData.errors[errorKey][0];

                if (
                    errorData.code === 'ValidationError' &&
                    errorKey === errorKeys.passwordValidation
                ) {
                    setError('password', { message: `${errorMessage}` });
                }

                if (
                    errorData.code === 'ValidationError' &&
                    (errorKey === errorKeys.alreadyRegisteredEmail ||
                        errorKey === errorKeys.notFoundEmail)
                ) {
                    setError('email', { message: `${errorMessage}` });
                }
            });
    };

    return (
        <div
            className={cn(
                'my-auto flex-1 flex flex-col gap-6 rounded-2.5xl bg-popover-10',
                isMobile ? 'grow' : 'max-w-[20.5rem]',
            )}
        >
            <div className="flex flex-col items-center gap-4 text-center">
                <OwnInnIcon className="size-11" />
                <div className="w-[20.5rem] flex flex-col items-center gap-2">
                    <h3 className="text-2xl font-semibold select-none">
                        {t('auth.signUpForm.text.title')}
                    </h3>
                    <div className="flex justify-center gap-1.5">
                        <p className="text-sm text-popover-800 font-medium">
                            {t('auth.signUpForm.text.subtitle')}
                        </p>
                        <Button
                            onClick={() => navigate('/auth/login')}
                            type="button"
                            variant="link"
                            className="p-0 hover:text-popover-800 active:text-popover-600"
                        >
                            {t('elements.buttons.login')}
                        </Button>
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-4">
                    <ETextInput
                        control={control}
                        fieldName={'email'}
                        label={t('elements.form.email.label')}
                        placeholder={t('your@email.com')}
                    />
                    <EPasswordInput
                        control={control}
                        fieldName={'password'}
                        label={t('elements.form.password.label')}
                        placeholder={t('elements.form.password.placeholder')}
                    />
                    <EPasswordInput
                        control={control}
                        fieldName={'confirmPassword'}
                        label={t('elements.form.confirmPassword.label')}
                        placeholder={t('elements.form.confirmPassword.placeholder')}
                    />
                </div>
                <ELoadingButton
                    isLoading={false}
                    onClick={handleSubmit(handleFormSubmit)}
                    text={t('elements.buttons.signUp')}
                />
                <span className="text-sm text-popover-800 text-center">
                    {t('auth.signUpForm.text.warning')}
                </span>
            </div>
        </div>
    );
};

export default SignUpForm;
